import { useState, useEffect } from 'react';
import { X, Smartphone, Share, Plus, MoreVertical } from 'lucide-react';

export default function InstallAppBanner({ primaryColor = '#9B1B30' }) {
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Detect device
    const userAgent = navigator.userAgent || navigator.vendor;
    const iOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const android = /android/i.test(userAgent);
    
    setIsIOS(iOS);
    setIsAndroid(android);

    // Show banner only on mobile and if not installed
    const isMobile = iOS || android || /webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const dismissed = localStorage.getItem('installBannerDismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
    
    // Show again after 24 hours
    if (isMobile && !standalone && (!dismissed || hoursSinceDismissed > 24)) {
      // Show immediately
      setShowBanner(true);
    }

    // Listen for install prompt (Android Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('installBannerDismissed', Date.now().toString());
  };

  if (!showBanner || isStandalone) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div 
        className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden animate-slideUp"
        style={{ animation: 'slideUp 0.3s ease-out' }}
      >
        {/* Header con gradiente */}
        <div 
          className="p-6 text-center text-white relative"
          style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` }}
        >
          <button 
            onClick={handleDismiss}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X size={18} />
          </button>
          
          <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-2xl flex items-center justify-center shadow-lg">
            <span className="text-5xl">🥩</span>
          </div>
          
          <h2 className="text-2xl font-bold mb-2">Scarica l'App!</h2>
          <p className="text-white/90 text-sm">
            Accesso veloce, notifiche ordini e funziona offline
          </p>
        </div>

        {/* Istruzioni */}
        <div className="p-6">
          {isIOS ? (
            // Istruzioni iOS
            <div className="space-y-4">
              <p className="text-center font-semibold text-gray-800 mb-4">
                📱 Segui questi 3 passi:
              </p>
              
              <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Share className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-800">1. Tocca Condividi</p>
                  <p className="text-sm text-gray-600">Il pulsante <strong>⬆️</strong> in basso nella barra</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-800">2. Scorri e trova</p>
                  <p className="text-sm text-gray-600"><strong>"Aggiungi a Home"</strong> e toccalo</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">✓</span>
                </div>
                <div>
                  <p className="font-bold text-gray-800">3. Conferma</p>
                  <p className="text-sm text-gray-600">Tocca <strong>"Aggiungi"</strong> in alto a destra</p>
                </div>
              </div>
            </div>
          ) : isAndroid && deferredPrompt ? (
            // Android con prompt nativo
            <div className="text-center">
              <p className="text-gray-600 mb-6">
                Installa l'app per un accesso più veloce e ricevere notifiche sui tuoi ordini!
              </p>
              <button
                onClick={handleInstall}
                className="w-full py-4 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: primaryColor }}
              >
                <Smartphone size={24} />
                Installa Ora
              </button>
            </div>
          ) : (
            // Android senza prompt (Chrome menu)
            <div className="space-y-4">
              <p className="text-center font-semibold text-gray-800 mb-4">
                📱 Segui questi 2 passi:
              </p>
              
              <div className="flex items-center gap-4 p-3 bg-green-50 rounded-xl">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MoreVertical className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-800">1. Tocca il Menu</p>
                  <p className="text-sm text-gray-600">I <strong>3 puntini ⋮</strong> in alto a destra</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 p-3 bg-green-50 rounded-xl">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-800">2. Seleziona</p>
                  <p className="text-sm text-gray-600"><strong>"Aggiungi a schermata Home"</strong></p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <button
            onClick={handleDismiss}
            className="w-full py-3 text-gray-500 font-medium hover:text-gray-700 transition-colors"
          >
            Magari dopo
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
